// Pure logic of the warehouses pages (WarehousesInstructions.js,
// WarehouseDetail.js): the warehouses the AI contacts. API:
// GET/POST /api/logistics/warehouses-edn → [{id, name, email, emails[],
// instructions, clients[{id, name}], client_count, created_at, updated_at}],
// PUT/DELETE /api/logistics/warehouses-edn/{id}; the B2B clients for the
// picker and the « rattachés » tile come from GET /api/logistics/clients-edn.

export const LIST_FILTERS = ['all', 'noemail', 'noinstr'];
export const PAGE_SIZE = 25;

const EMAIL_RE = /^\S+@\S+\.\S+$/;

export const getWarehouseEmails = (warehouse) => {
  if (Array.isArray(warehouse?.emails) && warehouse.emails.length > 0) {
    return warehouse.emails.filter(Boolean);
  }
  return warehouse?.email ? [warehouse.email] : [];
};

export const hasInstructions = (warehouse) => Boolean(String(warehouse?.instructions || '').trim());

export const emptyWarehouseForm = () => ({ name: '', emails: [''], instructions: '', clientIds: [] });

export const warehouseFormFrom = (warehouse) => {
  const emails = getWarehouseEmails(warehouse);
  return {
    name: warehouse.name || '',
    emails: emails.length > 0 ? emails : [''],
    instructions: warehouse.instructions || '',
    clientIds: (warehouse.clients || []).map((c) => c.id),
  };
};

export const validateWarehouseForm = (form, t) => {
  if (!form.name.trim()) {
    return t('warehousesInstructions.nameRequired', 'Le nom est requis.');
  }
  const bad = form.emails.map((e) => e.trim()).filter(Boolean).find((e) => !EMAIL_RE.test(e));
  if (bad) {
    return t('warehousesInstructions.invalidEmail', 'Email invalide : {{email}}', { email: bad });
  }
  return null;
};

// POST / PUT body (CreateWarehouseEDNRequest / UpdateWarehouseEDNRequest).
export const warehousePayload = (form) => {
  const emails = [];
  form.emails.map((e) => e.trim()).filter(Boolean).forEach((e) => {
    if (!emails.some((x) => x.toLowerCase() === e.toLowerCase())) emails.push(e);
  });
  return {
    name: form.name.trim(),
    email: emails[0] || '',
    emails,
    instructions: form.instructions,
    client_ids: form.clientIds,
  };
};

export const matchesFilter = (warehouse, filter) => {
  if (filter === 'noemail') return getWarehouseEmails(warehouse).length === 0;
  if (filter === 'noinstr') return !hasInstructions(warehouse);
  return true;
};

export const matchesSearch = (warehouse, query) => {
  const q = String(query || '').toLowerCase();
  if (!q) return true;
  const name = (warehouse.name || '').toLowerCase();
  const emails = getWarehouseEmails(warehouse).join(' ').toLowerCase();
  const clientNames = (warehouse.clients || []).map((c) => c.name).join(' ').toLowerCase();
  return name.includes(q) || emails.includes(q) || clientNames.includes(q);
};

export const sortByName = (list) => [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }));

// Tiles and subtitle. `clients` null = the B2B list is unknown (no tile).
export const warehousesSummary = (warehouses, clients) => {
  const list = Array.isArray(warehouses) ? warehouses : [];
  const linkedIds = new Set();
  list.forEach((w) => (w.clients || []).forEach((c) => linkedIds.add(String(c.id))));
  const withEmail = list.filter((w) => getWarehouseEmails(w).length > 0).length;
  const withInstructions = list.filter(hasInstructions).length;
  const summary = {
    total: list.length,
    withEmail,
    noEmail: list.length - withEmail,
    withInstructions,
    noInstructions: list.length - withInstructions,
    linkedClients: linkedIds.size,
    clientsTotal: null,
    unlinkedClients: null,
  };
  if (Array.isArray(clients)) {
    summary.clientsTotal = clients.length;
    summary.unlinkedClients = clients.filter((c) => !linkedIds.has(String(c.id))).length;
  }
  return summary;
};

// « À vérifier »: a warehouse the AI cannot reach (no address), then one it
// contacts without any instruction.
export const warehousesAttention = (warehouses) => {
  const list = sortByName(Array.isArray(warehouses) ? warehouses : []);
  const items = [];
  const noEmail = list.filter((w) => getWarehouseEmails(w).length === 0);
  if (noEmail.length) {
    items.push({ key: 'noemail', kind: 'noemail', tone: 'danger', count: noEmail.length, names: noEmail.map((w) => w.name), id: noEmail.length === 1 ? noEmail[0].id : null });
  }
  const noInstr = list.filter((w) => !hasInstructions(w));
  if (noInstr.length) {
    items.push({ key: 'noinstr', kind: 'noinstr', tone: 'warning', count: noInstr.length, names: noInstr.map((w) => w.name), id: noInstr.length === 1 ? noInstr[0].id : null });
  }
  return items;
};
